/*
 * Apelidos legíveis para os tipos que saem do contrato.
 *
 * `src/api/gerado/admin.ts` é GERADO de `api/openapi-admin.yaml` e não deve ser
 * editado — `npm run tipos:conferir` reprova se ele divergir. Este arquivo é a
 * única camada que o resto do painel importa, para que uma reorganização do
 * gerador não se espalhe por cinquenta arquivos.
 */
import type { components, operations } from "./gerado/admin";

type Schemas = components["schemas"];

export type Problema = Schemas["Problema"];
export type SessaoAberta = Schemas["SessaoAberta"];
export type BoletoDoPainel = Schemas["BoletoDoPainel"];
export type ListaDeBoletos = Schemas["ListaDeBoletos"];
export type EventoDeBoleto = Schemas["EventoDeBoleto"];
export type VisaoGeral = Schemas["VisaoGeral"];
export type TecnicoDoBoleto = Schemas["TecnicoDoBoleto"];
export type Divergencia = Schemas["Divergencia"];
export type ListaDeDivergencias = Schemas["ListaDeDivergencias"];
export type IndicadorDeFilas = Schemas["IndicadorDeFilas"];
export type AmostraDaFilaMorta = Schemas["AmostraDaFilaMorta"];
export type ListaDeDocumentos = Schemas["ListaDeDocumentos"];
export type Sistema = Schemas["Sistema"];
export type ListaDeSistemas = Schemas["ListaDeSistemas"];
export type DetalheDoSistema = Schemas["DetalheDoSistema"];
export type ChaveDeApi = Schemas["ChaveDeApi"];
export type TetoDeConsumo = Schemas["TetoDeConsumo"];
export type AcessoDoSistema = Schemas["AcessoDoSistema"];
export type RedePermitida = Schemas["RedePermitida"];
export type RedeNova = Schemas["RedeNova"];
export type ListaDePessoas = Schemas["ListaDePessoas"];
export type PessoaDoPainel = ListaDePessoas["pessoas"][number];
export type Convenio = Schemas["Convenio"];
export type ListaDeConvenios = Schemas["ListaDeConvenios"];
export type DetalheDoConvenio = Schemas["DetalheDoConvenio"];
export type EstadoDoIndicador = Schemas["EstadoDoIndicador"];
export type IndicadorInteiro = Schemas["IndicadorInteiro"];

/*
 * Os corpos de CADASTRO (change 20d).
 *
 * Tipados pelo contrato, e não escritos à mão: um formulário que mande um campo
 * que a API não conhece leva `400` — a rota usa `DisallowUnknownFields` —, e o
 * lugar de descobrir isso é a compilação, não a tela.
 */
export type PessoaNova = Schemas["PessoaNova"];
export type ContaNova = Schemas["ContaNova"];
export type SistemaNovo = Schemas["SistemaNovo"];
export type EntregaConfigurada = Schemas["EntregaConfigurada"];
export type CadastroCriado = Schemas["CadastroCriado"];

/*
 * O cedente como entidade (change `add-cedente-como-entidade-do-painel`).
 *
 * "Cedente" é texto de tela: no contrato continuam `pessoa`, e `Beneficiario` nos
 * nomes que já existiam. O apelido não muda o nome do schema, para quem procura
 * no YAML achar o que está lendo aqui.
 *
 * A conta do painel se identifica por `conta_id`, e não por `id`. O catálogo de
 * pendências é enum FECHADO, ao contrário de `Situacao`: cada código tem uma ação
 * na tela, e código novo exige tela nova de qualquer forma.
 */
export type PessoaDetalhe = Schemas["PessoaDetalhe"];
export type AlteracaoDePessoa = Schemas["AlteracaoDePessoa"];
export type EnderecoDoBeneficiario = Schemas["EnderecoDoBeneficiario"];
export type EnderecoLido = Schemas["EnderecoLido"];
export type Prontidao = Schemas["Prontidao"];
export type Pendencia = Schemas["Pendencia"];
export type CodigoDePendencia = Schemas["CodigoDePendencia"];
export type ResumoDeProntidao = Schemas["ResumoDeProntidao"];
export type ContaDaPessoa = Schemas["ContaDaPessoa"];
export type ListaDeContas = Schemas["ListaDeContas"];
export type ResumoDeConvenio = Schemas["ResumoDeConvenio"];
export type AlteracaoDeConta = Schemas["AlteracaoDeConta"];
export type ConvenioNovo = Schemas["ConvenioNovo"];
export type AlteracaoDeConvenio = Schemas["AlteracaoDeConvenio"];
export type BancoDoCatalogo = Schemas["BancoDoCatalogo"];
export type ListaDeBancos = Schemas["ListaDeBancos"];
export type DefeitoDeCampo = Schemas["DefeitoDeCampo"];
export type MunicipioCandidato = Schemas["MunicipioCandidato"];

/*
 * Planos e contratos (change `add-planos-e-contratos-no-painel`).
 *
 * `limite_mensal` e `teto_excedente` são `number | null`, e o `null` tem
 * significado próprio: sem limite, ilimitado. Nenhuma tela pode tratá-lo como
 * zero — zero é franquia zero, o oposto.
 */
export type Plano = Schemas["Plano"];
export type ListaDePlanos = Schemas["ListaDePlanos"];
export type DetalheDoPlano = Schemas["DetalheDoPlano"];
export type ContratoDoPlano = Schemas["ContratoDoPlano"];
export type PlanoNovo = Schemas["PlanoNovo"];
export type AlteracaoDePlano = Schemas["AlteracaoDePlano"];
export type PlanoAlterado = Schemas["PlanoAlterado"];
export type PoliticaDeExcedente = Plano["politica_excedente"];
export type Contrato = Schemas["Contrato"];
export type ListaDeContratos = Schemas["ListaDeContratos"];
export type ContratoNovo = Schemas["ContratoNovo"];
export type EncerramentoDeContrato = Schemas["EncerramentoDeContrato"];
export type TrocaDePlano = Schemas["TrocaDePlano"];
export type TrocaRealizada = Schemas["TrocaRealizada"];

/*
 * Cobrança por cedente (change `add-cobranca-por-cedente`).
 *
 * `convenios_permitidos` tem TRÊS leituras, e nenhuma tela pode juntar duas:
 * `null` é todos os convênios do beneficiário, `[]` é nenhum, e a lista é só
 * aqueles. `contrato` nulo num cedente de aplicação `por_cedente` é "não
 * emite" — e em `por_sistema` é só "o contrato é o da aplicação".
 */
export type ModalidadeCobranca = Schemas["ModalidadeCobranca"];
export type CobrancaDoSistema = Schemas["CobrancaDoSistema"];
export type AlteracaoDeCobranca = Schemas["AlteracaoDeCobranca"];
export type ContratoDoPar = Schemas["ContratoDoPar"];
export type ConsumoDoPar = Schemas["ConsumoDoPar"];
export type MotivoNaoEmite = Schemas["MotivoNaoEmite"];
export type CedenteDoSistema = Schemas["CedenteDoSistema"];
export type ListaDeCedentes = Schemas["ListaDeCedentes"];
export type VinculoDoBeneficiario = Schemas["VinculoDoBeneficiario"];
export type ListaDeVinculosDoBeneficiario = Schemas["ListaDeVinculosDoBeneficiario"];
export type VinculoNovo = Schemas["VinculoNovo"];
export type Vinculo = Schemas["Vinculo"];
export type ConveniosDoVinculo = Schemas["ConveniosDoVinculo"];
export type SituacaoDoVinculo = Schemas["SituacaoDoVinculo"];
export type LimiteDoVinculo = Schemas["LimiteDoVinculo"];

/*
 * Faturamento (change `add-faturamento`).
 *
 * Tudo em centavos inteiros, e a competência é `AAAA-MM` em UTC. A prévia e a
 * simulação são calculadas e NUNCA gravadas; só o fechamento grava, e a fatura
 * gravada muda só de situação.
 */
export type Competencia = Schemas["Competencia"];
export type SituacaoDaFatura = Schemas["SituacaoDaFatura"];
export type ItemDeFatura = Schemas["ItemDeFatura"];
export type PagadorDaFatura = Schemas["PagadorDaFatura"];
export type ResumoDeFatura = Schemas["ResumoDeFatura"];
export type ListaDeFaturas = Schemas["ListaDeFaturas"];
export type Fatura = Schemas["Fatura"];
export type LinhaDeCalculo = Schemas["LinhaDeCalculo"];
export type PreviaDaCompetencia = Schemas["PreviaDaCompetencia"];
export type PedidoDeFechamento = Schemas["PedidoDeFechamento"];
export type AjusteSemDestino = Schemas["AjusteSemDestino"];
export type SimulacaoDeFechamento = Schemas["SimulacaoDeFechamento"];
export type ResultadoDoFechamento = Schemas["ResultadoDoFechamento"];
export type BaixaDeFatura = Schemas["BaixaDeFatura"];
export type CancelamentoDeFatura = Schemas["CancelamentoDeFatura"];
export type IndicadorDeFaturamento = NonNullable<VisaoGeral["faturamento"]>;

/**
 * O `409` do `PATCH /planos/{id}`.
 *
 * Com a change `add-faturamento`, o mesmo status carrega dois códigos:
 * `confirmacao_necessaria` (com contagem, marca e contratos) e `plano_faturado`
 * (sem extensão). Por isso os membros saíram de `required` no contrato, e quem
 * lê a confirmação confere o código antes — ver `confirmacaoNecessaria` em
 * `telas/planos/plano.ts`.
 */
export type ConflitoDoPlano =
  operations["alterarPlano"]["responses"]["409"]["content"]["application/problem+json"];

/** O `409 confirmacao_necessaria`, já conferido: a contagem ATUAL, quais e a marca. */
export type ConfirmacaoNecessaria = ConflitoDoPlano &
  Required<Pick<ConflitoDoPlano, "contratos_vigentes" | "marca_dos_vigentes" | "contratos">>;

/*
 * As ações operacionais (change `add-acoes-operacionais-do-painel`).
 *
 * `GET /boletos/{id}/pdf` tem dois corpos no mesmo `200`, e quem decide é o
 * `Content-Type`: ver `useVerPDF`.
 *
 * Em `EntregaDoPainel`, instante opcional, `boleto_id` e `boleto_situacao` chegam como
 * TEXTO VAZIO, e só `motivo_bloqueio` é nulo. Testar com `||`, e nunca com `??`:
 * `revogada_em: ""` já fez a tela tratar chave ativa como revogada.
 *
 * `LinhaDaAuditoria.acao` é texto, e não `AcaoDePainel`: a linha pode ser anterior ao
 * catálogo de hoje, e a tela mostra cru o que não conhece. `AcaoDePainel` é o do filtro.
 */
export type EntregaDoPainel = Schemas["EntregaDoPainel"];
export type ListaDeEntregas = Schemas["ListaDeEntregas"];
export type EstadoDaEntrega = Schemas["EstadoDaEntrega"];
export type EventoDeEntrega = Schemas["EventoDeEntrega"];
export type MotivoDeBloqueioDoReenvio = Schemas["MotivoDeBloqueioDoReenvio"];
export type ReenvioDeEntrega = Schemas["ReenvioDeEntrega"];
export type ReenvioAgendado = Schemas["ReenvioAgendado"];
export type AcaoDePainel = Schemas["AcaoDePainel"];
export type ListaDaAuditoria = Schemas["ListaDaAuditoria"];
export type LinhaDaAuditoria = Schemas["LinhaDaAuditoria"];
export type LinkDaFicha = Schemas["LinkDaFicha"];
export type SituacaoPedidaDoSistema = Schemas["SituacaoPedidaDoSistema"];
export type PedidoDeSituacaoDoSistema = Schemas["PedidoDeSituacaoDoSistema"];
export type CorrecaoDeNomeDoSistema = Schemas["CorrecaoDeNomeDoSistema"];

/*
 * O envio (change `fix-emissao-ambigua`).
 *
 * `sondavel`, `reprocessavel` e `reprocessamento_barrado_por` só vêm no detalhe, e a linha da
 * lista é o estado inicial do painel: compare com `true`. Ausente é "ninguém conferiu", e não
 * "pode". `possiveis_duplicidades` ausente no detalhe é "não há", e na linha da lista não diz
 * nada: por isso a confirmação de reprocessar só abre com `reprocessavel` verdadeiro.
 */
export type EstadoDoEnvio = Schemas["EstadoDoEnvio"];
export type TentativaDeEnvio = Schemas["TentativaDeEnvio"];
export type TituloOrfao = Schemas["TituloOrfao"];
export type TipoDeOrfao = TituloOrfao["tipo"];

/*
 * O certificado e o provisionamento (change `add-certificado-do-cedente`).
 *
 * Tudo aqui é metadado: nem arquivo, nem senha, nem `provider_ref`. `certificado` do convênio é
 * o EFETIVO, o menor vencimento entre os dois níveis, e `nivel` diz de qual deles veio. A
 * leitura do cedente traz a linha dele, e `null` é não haver linha.
 *
 * `etapa` não está em `Problema`: é extensão das recusas do provisionamento, e quem a lê é
 * `telas/cedentes/provisionamento.ts`.
 */
export type SituacaoDoCertificado = Schemas["SituacaoDoCertificado"];
export type CertificadoDoPainel = Schemas["CertificadoDoPainel"];
export type LeituraDoCertificado = Schemas["LeituraDoCertificado"];
export type ConferenciaDoCertificado = Schemas["ConferenciaDoCertificado"];
export type ProvisionamentoDoConvenio = Schemas["ProvisionamentoDoConvenio"];
export type EtapaDoProvisionamento = Schemas["EtapaDoProvisionamento"];
export type PedidoDeProvisionamento = Schemas["PedidoDeProvisionamento"];
export type CredencialDoCatalogo = NonNullable<BancoDoCatalogo["credenciais_webservice"]>[number];

/*
 * A situação é `string` no contrato, e isso é deliberado (doc 02 §8): o
 * conjunto é ABERTO, e um enum fechado faria este painel recusar resposta
 * válida no dia em que uma situação nova aparecesse.
 *
 * As conhecidas ficam aqui só para dar autocompletar e alimentar o mapa de
 * cores. Toda leitura precisa tratar o caso "não conheço esta" — ver
 * `aparenciaDaSituacao`.
 */
export const SITUACOES_CONHECIDAS = [
  "RECEBIDO",
  "ENFILEIRADO",
  "ENVIADO",
  "EMITIDO",
  "REGISTRADO",
  "FALHA",
  "LIQUIDADO",
  "BAIXADO",
  "PROTESTADO",
  "REJEITADO",
  "DESCARTADO",
  "CANCELADO",
] as const;

export type SituacaoConhecida = (typeof SITUACOES_CONHECIDAS)[number];

/** Uma situação vinda da API: conhecida ou não. */
export type Situacao = SituacaoConhecida | (string & {});

/*
 * Os papéis, por força crescente: `leitura` ⊂ `operador` ⊂ `admin`.
 *
 * Diferente da situação, este conjunto é fechado — ele é a definição da
 * autorização, e um papel novo exigiria mudar tela de qualquer forma. O que o
 * painel faz com um papel desconhecido é o oposto de ignorar: não concede nada
 * (ver `sessao/papeis.ts`).
 */
export const PAPEIS = ["leitura", "operador", "admin"] as const;
export type Papel = (typeof PAPEIS)[number];
